// Deep links (contract D.5): https://whsmacon.com/ds.php?x=<kind>.<ver>.<token>
// token = base64url(UTF-8 JSON), no padding, [A-Za-z0-9_-], at most 2000
// chars. Kinds: find v1 and radio v1. ds.php only forwards tokens that match
// /^(find|radio)\.1\.[A-Za-z0-9_-]{1,2000}$/.
//
// find.1 JSON (short keys on purpose; optional keys may be omitted):
//   { b, s:[[key, slot, jam]], i:[0|1], n:[key], w:[[outer, inner]], ve, c, y:[from, to],
//     e, sbd:0|1, vb, m:'all'|'min'|'closest', mn, o }
// radio.1 JSON:
//   { b, md:'vibe'|'walk'|'song', vb, e, y:[from, to], sbd:0|1, sd:seed, s:song, or:order, pa:path, la:0|1 }
//
// Names are never in a token: find chips resolve through the catalog
// (fetchCatalogSongs / fetchVenues / fetchCities).
//
// The ds.php?set= format is NOT built here: App.js shareUrl() owns it and
// stays byte-identical.

import { toast } from 'react-toastify';
import { bandByKey } from './bands';
import { emptyQuery, normalizeQuery, SLOTS } from './search/queryOps';

export const LINK_BASE = 'https://whsmacon.com/ds.php?x=';
export const TOKEN_MAX = 2000;

const SONG_KEY = /^[a-z0-9-]{1,80}$/;
const VENUE_ID = /^[0-9a-f]{6,12}$/;
const CITY_KEY = /^[^|]{1,60}\|[A-Za-z0-9 ]{0,12}$/;
const NAME_KEY = /^[A-Za-z0-9_-]{1,40}$/; // era and vibe keys
const SORTS = ['date', '-date', 'rating', 'downloads', 'jams', 'rare'];
const RADIO_MODES = ['vibe', 'walk', 'song'];
const RADIO_ORDERS = ['chrono', 'monster', 'shuffle'];
const RADIO_PATHS = ['classic', 'adventurous'];

const notify = (msg, type) => {
  try {
    toast(msg, { type: type || toast.TYPE.INFO });
  } catch (e) {
    /* toasts are best-effort */
  }
};

// ---- base64url over UTF-8 (no btoa/TextEncoder: works everywhere) ----------

const ALPHA = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

const utf8Bytes = (str) => {
  const enc = encodeURIComponent(str); // throws URIError on lone surrogates
  const out = [];
  for (let i = 0; i < enc.length; i++) {
    if (enc[i] === '%') {
      out.push(parseInt(enc.substr(i + 1, 2), 16));
      i += 2;
    } else {
      out.push(enc.charCodeAt(i));
    }
  }
  return out;
};

const utf8String = (bytes) => {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += '%' + (bytes[i] < 16 ? '0' : '') + bytes[i].toString(16);
  return decodeURIComponent(s); // throws URIError on invalid UTF-8
};

const b64urlEncode = (bytes) => {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const n = (bytes[i] << 16) | ((bytes[i + 1] || 0) << 8) | (bytes[i + 2] || 0);
    out += ALPHA[(n >> 18) & 63] + ALPHA[(n >> 12) & 63];
    if (i + 1 < bytes.length) out += ALPHA[(n >> 6) & 63];
    if (i + 2 < bytes.length) out += ALPHA[n & 63];
  }
  return out;
};

const b64urlDecode = (s) => {
  if (s.length % 4 === 1) return null;
  const out = [];
  let buf = 0;
  let bits = 0;
  for (let i = 0; i < s.length; i++) {
    const v = ALPHA.indexOf(s[i]);
    if (v < 0) return null;
    buf = ((buf << 6) | v) & 0xffffff;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out.push((buf >> bits) & 255);
    }
  }
  return out;
};

const encodeJson = (obj) => {
  try {
    return b64urlEncode(utf8Bytes(JSON.stringify(obj)));
  } catch (e) {
    return null;
  }
};

// ---- validation (strict: any malformed field rejects the token) -----------

const isInt = (v) => typeof v === 'number' && Number.isInteger(v);
const isYear = (v) => isInt(v) && v >= 1900 && v <= 2100;
const flag = (v) => v === 1 || v === true;
const isFlag = (v) => v == null || v === 0 || v === 1 || v === true || v === false;
const optMatch = (v, re) => v == null || (typeof v === 'string' && re.test(v));
const knownBand = (b) => typeof b === 'string' && !!bandByKey(b);

// null (absent) | [from|null, to|null] with at least one year
const cleanYears = (y) => {
  if (y == null) return { ok: true, v: null };
  if (!Array.isArray(y) || y.length !== 2) return { ok: false };
  if (!y.every((x) => x == null || isYear(x)) || y.every((x) => x == null)) return { ok: false };
  return { ok: true, v: [y[0] == null ? null : y[0], y[1] == null ? null : y[1]] };
};

// Returns sanitized, fully populated find data, or null. Idempotent.
const validateFind = (d) => {
  if (!d || typeof d !== 'object' || Array.isArray(d) || !knownBand(d.b)) return null;
  const s = d.s == null ? [] : d.s;
  if (!Array.isArray(s) || s.length > 12) return null;
  const songs = [];
  for (let k = 0; k < s.length; k++) {
    const e = s[k];
    if (!Array.isArray(e) || !SONG_KEY.test(e[0] || '')) return null;
    const slot = e[1] == null ? 'any' : e[1];
    const jam = e[2] == null ? null : e[2];
    if (SLOTS.indexOf(slot) < 0 || !(jam === null || jam === 75 || jam === 90)) return null;
    songs.push([e[0], slot, jam]);
  }
  const iRaw = d.i == null ? [] : d.i;
  if (!Array.isArray(iRaw) || iRaw.length > Math.max(0, songs.length - 1) || !iRaw.every((x) => x === 0 || x === 1 || x === true || x === false)) {
    return null;
  }
  const links = [];
  for (let k = 0; k < Math.max(0, songs.length - 1); k++) links.push(flag(iRaw[k]) ? 1 : 0);
  const n = d.n == null ? [] : d.n;
  if (!Array.isArray(n) || n.length > 4 || !n.every((x) => typeof x === 'string' && SONG_KEY.test(x))) return null;
  const w = d.w == null ? [] : d.w;
  if (
    !Array.isArray(w) ||
    w.length > 3 ||
    !w.every((p) => Array.isArray(p) && p.length === 2 && p.every((x) => typeof x === 'string' && SONG_KEY.test(x)))
  ) {
    return null;
  }
  const years = cleanYears(d.y);
  if (!years.ok) return null;
  if (!optMatch(d.ve, VENUE_ID) || !optMatch(d.c, CITY_KEY) || !optMatch(d.e, NAME_KEY) || !optMatch(d.vb, NAME_KEY)) return null;
  if (!isFlag(d.sbd)) return null;
  const m = d.m == null ? 'all' : d.m;
  if (['all', 'min', 'closest'].indexOf(m) < 0) return null;
  const mn = d.mn == null ? 2 : d.mn;
  if (!isInt(mn) || mn < 1 || mn > 12) return null;
  const o = d.o == null ? 'date' : d.o;
  if (SORTS.indexOf(o) < 0) return null;
  return {
    b: d.b,
    s: songs,
    i: links,
    n: n.slice(),
    w: w.map((p) => [p[0], p[1]]),
    ve: d.ve || null,
    c: d.c || null,
    y: years.v,
    e: d.e || null,
    sbd: flag(d.sbd) ? 1 : 0,
    vb: d.vb || null,
    m,
    mn,
    o,
  };
};

// Returns sanitized, fully populated radio data, or null. Idempotent.
const validateRadio = (d) => {
  if (!d || typeof d !== 'object' || Array.isArray(d) || !knownBand(d.b)) return null;
  const md = d.md == null ? 'vibe' : d.md;
  if (RADIO_MODES.indexOf(md) < 0) return null;
  if (!optMatch(d.vb, NAME_KEY) || !optMatch(d.e, NAME_KEY) || !optMatch(d.s, SONG_KEY)) return null;
  const years = cleanYears(d.y);
  if (!years.ok || !isFlag(d.sbd) || !isFlag(d.la)) return null;
  if (d.sd != null && !(isInt(d.sd) && d.sd >= 0 && d.sd <= 4294967295)) return null;
  if (d.or != null && RADIO_ORDERS.indexOf(d.or) < 0) return null;
  if (d.pa != null && RADIO_PATHS.indexOf(d.pa) < 0) return null;
  if ((md === 'walk' || md === 'song') && !d.s) return null; // B.10: song required
  return {
    b: d.b,
    md,
    vb: d.vb || null,
    e: d.e || null,
    y: years.v,
    sbd: flag(d.sbd) ? 1 : 0,
    sd: d.sd == null ? null : d.sd,
    s: d.s || null,
    or: d.or || null,
    pa: d.pa || null,
    la: flag(d.la) ? 1 : 0,
  };
};

const VALIDATORS = { find: validateFind, radio: validateRadio };

// ---- encode ---------------------------------------------------------------

const findData = (bandKey, q) => {
  const n = normalizeQuery(q);
  const d = { b: bandKey, s: n.songs.map((s) => [s.key, s.slot, s.jam]) };
  if (n.links.some(Boolean)) d.i = n.links.map((l) => (l ? 1 : 0));
  if (n.not.length) d.n = n.not.map((x) => x.key);
  if (n.sandwiches.length) d.w = n.sandwiches.map((w) => [w.outer.key, w.inner.key]);
  if (n.venue) d.ve = n.venue.id;
  if (n.city) d.c = n.city.key;
  if (n.years) d.y = n.years;
  if (n.era) d.e = n.era;
  if (n.sbd) d.sbd = 1;
  if (n.vibe) d.vb = n.vibe;
  if (n.mode !== 'all') d.m = n.mode;
  if (n.mode === 'min') d.mn = n.minN;
  if (n.sort !== 'date') d.o = n.sort;
  return d;
};

const radioData = (bandKey, p) => {
  const r = p || {};
  const d = { b: bandKey, md: r.mode || 'vibe' };
  if (r.vibe) d.vb = r.vibe;
  if (r.era) d.e = r.era;
  if (r.from != null || r.to != null) d.y = [r.from == null ? null : Number(r.from), r.to == null ? null : Number(r.to)];
  if (r.sbd) d.sbd = 1;
  if (r.seed != null && r.seed !== '') d.sd = Number(r.seed);
  if (r.song) d.s = r.song;
  if (r.order) d.or = r.order;
  if (r.path) d.pa = r.path;
  if (r.land) d.la = 1;
  return d;
};

// Encode, validate the result through the same decoder, and enforce length.
const encodeToken = (kind, data) => {
  if (!VALIDATORS[kind](data)) return null;
  const tok = encodeJson(data);
  if (!tok || tok.length > TOKEN_MAX) return null;
  return kind + '.1.' + tok;
};

// 'find.1.<b64url>', or null when the band is unknown, the query is malformed,
// or the token would pass 2000 chars. SavedSearches stores this string.
export const encodeFindToken = (bandKey, q) => encodeToken('find', findData(bandKey, q));

export const encodeRadioToken = (bandKey, params) => encodeToken('radio', radioData(bandKey, params));

// Full share links, or null (see encodeFindToken). Callers should show a
// toast instead of copying when null comes back.
export const buildFindLink = (bandKey, q) => {
  const t = encodeFindToken(bandKey, q);
  return t ? LINK_BASE + t : null;
};

// params = radioSource.params() (includes the seed; more/less are not linked)
export const buildRadioLink = (bandKey, params) => {
  const t = encodeRadioToken(bandKey, params);
  return t ? LINK_BASE + t : null;
};

// ---- decode ---------------------------------------------------------------

// 'find.1.<token>' | 'radio.1.<token>' -> { kind, ver: 1, data } | null.
// `data` is sanitized and fully populated. Never throws, never toasts.
export const decodeToken = (str) => {
  try {
    if (typeof str !== 'string') return null;
    const m = /^(find|radio)\.(\d{1,3})\.([A-Za-z0-9_-]+)$/.exec(str);
    if (!m || m[2] !== '1' || m[3].length > TOKEN_MAX) return null;
    const bytes = b64urlDecode(m[3]);
    if (!bytes) return null;
    const data = VALIDATORS[m[1]](JSON.parse(utf8String(bytes)));
    return data ? { kind: m[1], ver: 1, data } : null;
  } catch (e) {
    return null;
  }
};

// location.search -> { kind, ver, data } | null. No x= param: null, silently.
// An x= that fails validation: null plus the toast "This link could not be
// opened." Never throws.
export const parseX = (search) => {
  let x = null;
  try {
    x = new URLSearchParams(search || '').get('x');
  } catch (e) {
    x = null;
  }
  if (!x) return null;
  const out = decodeToken(x);
  if (!out) notify('This link could not be opened.', toast.TYPE && toast.TYPE.ERROR);
  return out;
};

// find data + catalog { songs, venues, cities } (the fetchCatalogSongs /
// fetchVenues / fetchCities rows) -> { query: FindQuery, droppedSongs }.
// Song keys missing from a loaded catalog are dropped (their runs split);
// with no catalog loaded, keys are kept and the key stands in for the name.
// Unknown venue / city ids are dropped the same way.
export const resolveFindToken = (data, catalog) => {
  const d = validateFind(data);
  const q = emptyQuery();
  if (!d) return { query: q, droppedSongs: 0 };
  const cat = catalog || {};
  const index = (rows, key) => {
    const m = {};
    (Array.isArray(rows) ? rows : []).forEach((r) => {
      if (r && r[key] != null) m[r[key]] = r;
    });
    return m;
  };
  const songs = index(cat.songs, 'key');
  const haveSongs = Array.isArray(cat.songs) && cat.songs.length > 0;
  const unknown = {};
  const known = (k) => {
    if (!haveSongs || songs[k]) return true;
    unknown[k] = true;
    return false;
  };
  const song = (k) => ({ key: k, name: (songs[k] && songs[k].name) || k });

  const kept = [];
  d.s.forEach((e, i) => known(e[0]) && kept.push(i));
  q.songs = kept.map((i) => ({ ...song(d.s[i][0]), slot: d.s[i][1], jam: d.s[i][2] }));
  q.links = [];
  for (let a = 0; a < kept.length - 1; a++) q.links.push(kept[a + 1] === kept[a] + 1 && d.i[kept[a]] === 1);
  q.not = d.n.filter(known).map(song);
  q.sandwiches = d.w
    .filter((p) => {
      const outerOk = known(p[0]); // both checked, so both unknown keys get counted
      const innerOk = known(p[1]);
      return outerOk && innerOk;
    })
    .map((p) => ({ outer: song(p[0]), inner: song(p[1]) }));

  if (d.ve) {
    const haveVenues = Array.isArray(cat.venues) && cat.venues.length > 0;
    const v = index(cat.venues, 'id')[d.ve];
    q.venue = v ? { id: v.id, name: v.name } : haveVenues ? null : { id: d.ve, name: d.ve };
  }
  if (d.c) {
    const haveCities = Array.isArray(cat.cities) && cat.cities.length > 0;
    const c = index(cat.cities, 'key')[d.c];
    q.city = c ? { key: c.key, label: c.label } : haveCities ? null : { key: d.c, label: d.c.split('|')[0] };
  }
  q.years = d.y;
  q.era = d.e;
  q.sbd = d.sbd === 1;
  q.vibe = d.vb;
  q.mode = d.m;
  q.minN = d.mn;
  q.sort = d.o;
  return { query: q, droppedSongs: Object.keys(unknown).length };
};

// Same as resolveFindToken, returning just the FindQuery. Toasts
// "N songs in this link are no longer in the catalog." when songs were
// dropped (pass { notify: false } to stay quiet).
export const findTokenToQuery = (data, catalog, opts) => {
  const { query, droppedSongs } = resolveFindToken(data, catalog);
  if (droppedSongs > 0 && !(opts && opts.notify === false)) {
    notify(
      droppedSongs === 1
        ? '1 song in this link is no longer in the catalog.'
        : droppedSongs + ' songs in this link are no longer in the catalog.'
    );
  }
  return query;
};

// radio data -> api.radio params { mode, vibe, era, from, to, sbd, song, order,
// path, land, seed } (unset keys omitted; radioSource makes a seed if absent).
export const radioTokenToParams = (data) => {
  const d = validateRadio(data);
  if (!d) return null;
  const p = { mode: d.md };
  if (d.vb) p.vibe = d.vb;
  if (d.e) p.era = d.e;
  if (d.y && d.y[0] != null) p.from = d.y[0];
  if (d.y && d.y[1] != null) p.to = d.y[1];
  if (d.sbd) p.sbd = true;
  if (d.s) p.song = d.s;
  if (d.or) p.order = d.or;
  if (d.pa) p.path = d.pa;
  if (d.la) p.land = true;
  if (d.sd != null) p.seed = d.sd;
  return p;
};
